/**
 * Asistente de Relinkeo Proforma ↔ DTE (Iter 236.r5, Carlos).
 *
 * Deep-link desde el correo de alerta "Auto-link de Proforma falló":
 *   /contabilidad/relinkear-proforma?proforma=99999&dte=2470&tipo_dte=33
 *
 * Flujo:
 *  1. Muestra los datos del DTE (folio, cliente, fecha, total) — auto-cargado.
 *  2. Lista las proformas pendientes del mismo cliente para sugerir match rápido.
 *  3. Permite tipear número manual si la proforma correcta no aparece.
 *  4. Pide motivo (>=5 chars, auditoría) y dispara el endpoint
 *     POST /api/facturacion/proformas/{id}/relinkear-dte.
 *  5. El backend valida que el cliente del DTE coincida con el de la proforma
 *     (anti-error). Si no coincide, devuelve 400 con detalle claro.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Link2,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  ArrowLeft,
  Search,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const fmtCLP = (n) => `$${(Number(n) || 0).toLocaleString('es-CL')}`;
const fmtDate = (s) => (s ? String(s).split('T')[0] : '—');

export default function RelinkearProformaPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const proformaNumDesdeURL = params.get('proforma') || '';
  const folioDTE = params.get('dte') || '';
  const tipoDTE = params.get('tipo_dte') || '33';

  const [loading, setLoading] = useState(true);
  const [dteData, setDteData] = useState(null);
  const [proformasPendientes, setProformasPendientes] = useState([]);
  const [proformaSeleccionada, setProformaSeleccionada] = useState(null);
  const [folioManual, setFolioManual] = useState('');
  const [motivo, setMotivo] = useState(
    proformaNumDesdeURL
      ? `Auto-link falló porque PRO${proformaNumDesdeURL} no existe. Corregido manualmente.`
      : '',
  );
  const [submitting, setSubmitting] = useState(false);
  const [filtroProforma, setFiltroProforma] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Buscar el DTE para obtener datos del cliente
      const dteRes = await api.get('/dte/documentos', {
        params: { folio: folioDTE, tipo: tipoDTE, page_size: 5 },
      });
      const dtes = dteRes.data?.documentos || [];
      const dte = dtes.find((d) => String(d.folio) === String(folioDTE));
      if (dte) {
        setDteData(dte);
        // 2. Pedir proformas pendientes del mismo cliente
        const rutCliente = dte.receptor?.rut || '';
        if (rutCliente) {
          try {
            const pfRes = await api.get('/facturacion/proformas', {
              params: { cliente_rut: rutCliente },
            });
            const all = pfRes.data?.proformas || pfRes.data || [];
            // Filtrar solo las que están pendientes de DTE
            const pendientes = (Array.isArray(all) ? all : []).filter((p) =>
              ['aprobada', 'facturada_en_alegra'].includes(p.estado),
            );
            setProformasPendientes(pendientes);
          } catch {
            setProformasPendientes([]);
          }
        }
      } else {
        toast.warning(`DTE folio ${folioDTE} no encontrado`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error cargando DTE');
    } finally {
      setLoading(false);
    }
  }, [folioDTE, tipoDTE]);

  useEffect(() => {
    if (folioDTE) reload();
    else setLoading(false);
  }, [folioDTE, reload]);

  const proformasFiltradas = useMemo(() => {
    if (!proformasPendientes?.length) return [];
    const q = filtroProforma.trim().toLowerCase();
    if (!q) return proformasPendientes;
    return proformasPendientes.filter(
      (p) =>
        String(p.numero_proforma || '').includes(q) ||
        String(p.descripcion || '').toLowerCase().includes(q) ||
        String(p.ot_numero || '').includes(q),
    );
  }, [proformasPendientes, filtroProforma]);

  const handleSubmit = async () => {
    const pf = proformaSeleccionada;
    if (!pf) {
      toast.error('Selecciona una proforma de la lista o usa el linkeo manual abajo');
      return;
    }
    if (motivo.trim().length < 5) {
      toast.error('El motivo debe tener al menos 5 caracteres (auditoría)');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/facturacion/proformas/${pf.id}/relinkear-dte`, {
        folio_dte: folioDTE,
        motivo: motivo.trim(),
      });
      toast.success(
        `Proforma #${pf.numero_proforma} linkeada al DTE folio ${folioDTE} exitosamente`,
      );
      setTimeout(() => navigate('/contabilidad/notas-credito'), 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error linkeando');
    } finally {
      setSubmitting(false);
    }
  };

  const handleManualLink = async () => {
    if (!folioManual.trim()) {
      toast.error('Ingresa el número de proforma manual');
      return;
    }
    // Buscar la proforma por número y mostrarla como "seleccionada"
    const numero = parseInt(folioManual.trim(), 10);
    if (!numero) {
      toast.error('El número debe ser un entero');
      return;
    }
    try {
      const pfRes = await api.get('/facturacion/proformas');
      const proformas = pfRes.data?.proformas || pfRes.data || [];
      const pf = Array.isArray(proformas)
        ? proformas.find((p) => p.numero_proforma === numero)
        : null;
      if (!pf) {
        toast.error(`Proforma #${numero} no existe en la BD`);
        return;
      }
      setProformaSeleccionada(pf);
      toast.success(`Proforma #${numero} cargada — confirma con el botón "Linkear"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error buscando proforma');
    }
  };

  if (!folioDTE) {
    return (
      <div className="p-6 max-w-2xl mx-auto" data-testid="relinkear-page">
        <Card>
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
            <h2 className="text-xl font-bold mb-2">Falta información</h2>
            <p className="text-gray-600">
              Esta página espera un parámetro <code>?dte=NNNN</code> en la URL.
              Por lo general llegás acá desde el correo de alerta.
            </p>
            <Button className="mt-4" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5" data-testid="relinkear-page">
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-2">
          <ArrowLeft className="w-4 h-4 mr-1" />
          Volver
        </Button>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Link2 className="w-6 h-6 text-blue-600" />
          Asistente de Relinkeo: Proforma ↔ DTE
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Linkea manualmente una proforma a un DTE de venta. Usá esto cuando el
          auto-link falló (típicamente porque el DTE traía un número de proforma
          equivocado o inexistente).
        </p>
      </div>

      {/* ───────── DTE de origen ───────── */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4" />
            DTE de venta a linkear
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Cargando datos del DTE…
            </div>
          ) : dteData ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <span className="text-gray-500 text-xs">Folio</span>
                <div className="font-mono font-semibold">{dteData.folio}</div>
              </div>
              <div>
                <span className="text-gray-500 text-xs">Fecha</span>
                <div>{fmtDate(dteData.fecha_emision)}</div>
              </div>
              <div>
                <span className="text-gray-500 text-xs">Cliente</span>
                <div className="truncate" title={dteData.receptor?.razon_social}>
                  {dteData.receptor?.razon_social || '—'}
                </div>
                <div className="text-xs text-gray-400">{dteData.receptor?.rut}</div>
              </div>
              <div>
                <span className="text-gray-500 text-xs">Total</span>
                <div className="font-semibold">{fmtCLP(dteData.totales?.total)}</div>
              </div>
              {proformaNumDesdeURL && (
                <div className="col-span-full bg-amber-50 border border-amber-200 rounded p-2 text-xs">
                  <AlertTriangle className="w-3.5 h-3.5 inline text-amber-600 mr-1" />
                  El DTE referenciaba <b>Proforma #{proformaNumDesdeURL}</b>, pero no
                  existe en la BD. Selecciona la proforma correcta abajo.
                </div>
              )}
            </div>
          ) : (
            <div className="text-red-600 text-sm">
              No se encontró el DTE folio {folioDTE} (tipo {tipoDTE}). Verifica el link
              del correo.
            </div>
          )}
        </CardContent>
      </Card>

      {/* ───────── Lista de proformas sugeridas ───────── */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            Proformas pendientes del mismo cliente ({proformasPendientes.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 mb-3">
            <Search className="w-4 h-4 text-gray-400" />
            <Input
              placeholder="Buscar por número, descripción o OT…"
              value={filtroProforma}
              onChange={(e) => setFiltroProforma(e.target.value)}
              className="max-w-md"
              data-testid="filtro-proforma"
            />
          </div>
          {proformasFiltradas.length === 0 ? (
            <div className="text-gray-500 text-sm text-center py-6 border rounded">
              No hay proformas pendientes para este cliente.
              <br />
              <span className="text-xs">Usa el linkeo manual abajo si conoces el número.</span>
            </div>
          ) : (
            <div className="border rounded max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1">N°</th>
                    <th className="text-left px-2 py-1">Descripción</th>
                    <th className="text-left px-2 py-1">OT</th>
                    <th className="text-left px-2 py-1">Estado</th>
                    <th className="text-right px-2 py-1">Total</th>
                    <th className="text-center px-2 py-1">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {proformasFiltradas.map((pf) => {
                    const selected = proformaSeleccionada?.id === pf.id;
                    return (
                      <tr
                        key={pf.id}
                        className={`border-t hover:bg-blue-50 ${
                          selected ? 'bg-blue-100' : ''
                        }`}
                        data-testid={`proforma-row-${pf.numero_proforma}`}
                      >
                        <td className="px-2 py-1 font-mono font-semibold">
                          {pf.numero_proforma}
                        </td>
                        <td className="px-2 py-1 truncate max-w-md" title={pf.descripcion}>
                          {pf.descripcion || '—'}
                        </td>
                        <td className="px-2 py-1 text-gray-500">{pf.ot_numero || '—'}</td>
                        <td className="px-2 py-1">
                          <Badge variant="outline" className="text-xs">
                            {pf.estado}
                          </Badge>
                        </td>
                        <td className="px-2 py-1 text-right">
                          {fmtCLP((pf.totales || {}).total || pf.total)}
                        </td>
                        <td className="px-2 py-1 text-center">
                          <Button
                            size="sm"
                            variant={selected ? 'default' : 'outline'}
                            onClick={() => setProformaSeleccionada(pf)}
                            data-testid={`btn-elegir-pf-${pf.numero_proforma}`}
                          >
                            {selected ? '✓ Elegida' : 'Elegir'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ───────── Linkeo manual por número ───────── */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">¿No aparece? Linkeo manual</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 items-end max-w-md">
            <div className="flex-1">
              <Label className="text-xs">Número de proforma</Label>
              <Input
                placeholder="Ej: 24"
                value={folioManual}
                onChange={(e) => setFolioManual(e.target.value)}
                data-testid="folio-manual-input"
              />
            </div>
            <Button onClick={handleManualLink} variant="outline" data-testid="btn-buscar-manual">
              Buscar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ───────── Motivo + Confirmar ───────── */}
      <Card className="border-green-200 bg-green-50/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Confirmar linkeo</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {proformaSeleccionada ? (
            <div className="bg-white border rounded p-3 text-sm">
              <span className="text-gray-500">Vas a linkear:</span>
              <div className="mt-1">
                <b>Proforma #{proformaSeleccionada.numero_proforma}</b> —{' '}
                {proformaSeleccionada.descripcion}
              </div>
              <div className="text-xs text-gray-500">
                ↕ con el <b>DTE folio {folioDTE}</b> ({dteData?.receptor?.razon_social})
              </div>
            </div>
          ) : (
            <div className="text-gray-500 text-sm italic">
              Selecciona una proforma de la lista o búscala manualmente.
            </div>
          )}

          <div>
            <Label className="text-sm">
              Motivo / Observación <span className="text-red-500">*</span>
            </Label>
            <Textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ej: El DTE traía PRO99 por error, la real era PRO23"
              rows={2}
              data-testid="motivo-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              Mínimo 5 caracteres — queda en audit log para trazabilidad.
            </p>
          </div>

          <Button
            onClick={handleSubmit}
            disabled={submitting || !proformaSeleccionada || motivo.trim().length < 5}
            data-testid="btn-confirmar-linkeo"
            className="bg-green-600 hover:bg-green-700"
            size="lg"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <CheckCircle2 className="w-4 h-4 mr-2" />
            )}
            Linkear Proforma → DTE
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
